import { Utils } from '@common';
import { React, Styled } from '@common/3rd';
import { ContainerComponents, ContainerProps } from '@my-types';
import { GridContainer } from './container-abstract';

/**
 * 标准的, 即横向排列
 */
abstract class Container<P extends ContainerProps, S, C extends ContainerComponents> extends GridContainer<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'container'
			})`
				display: grid;
				grid-column-gap: ${props => props.theme.CONTAINER_CELL_HORIZONTAL_GAP}px;
				grid-row-gap: ${props => props.theme.CONTAINER_CELL_VERTICAL_GAP}px;
				align-items: start;
				grid-template-columns: ${() => this.buildGridTemplateAreas()};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	/**
	 * 描画由layout定义的children
	 */
	protected renderChildrenByLayout(): any {
		const layout = this.props.layout;
		if (layout == null) {
			// 没有设置layout
			return;
		}

		// 清掉原来的
		const elements = Utils.toArray(layout)
			.map((child: any, index: number) => {
				if (child == null) {
					return null;
				}
				const componentName = child.c;
				const type = this.getComponentRegistry().find(componentName);
				return React.createElement(
					type,
					this.buildLayoutForChild(type, child, index),
					this.buildChildrenForChild(type, child)
				);
			})
			.filter(elm => elm != null);
		return elements;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderChildren()}
				{this.renderChildrenByLayout()}
			</C.Box>
		);
	}
	/**
	 * 构造child的layout
	 */
	protected buildLayoutForChild(type: any, layout: any, index?: number): any {
		const newLayout = {
			key: index,
			model: this.getModel(),
			root: this.getRootModel()
		} as any;

		if (!Utils.isObject(layout)) {
			return newLayout;
		}

		layout = layout as any;
		// 把c和children, 以及组件自己指定的作为children的属性去掉
		let childKeys = {};
		if (type.getConcernedChildren) {
			childKeys = type.getConcernedChildren() || {};
		}
		Object.keys(layout)
			.filter(key => !(key in childKeys || key === 'c' || key === 'children'))
			.forEach(key => {
				newLayout[key] = layout[key];
			});
		return newLayout;
	}
	/**
	 * 构造child的children
	 */
	protected buildChildrenForChild(type: any, layout: any): [] | any | null {
		if (layout == null) {
			return null;
		}

		if (!Utils.isObject(layout)) {
			// 并不是对象, 但是却有值, 直接当做children返回
			return ([layout] as unknown) as [];
		}

		let childrenMapping = { children: null } as {
			[propName: string]: null | string;
		};
		if (type.getConcernedChildren) {
			// 组件指定的作为子组件关键字, 以及与组件名称的映射关系
			// children是默认子组件的关键字, 并且默认是数组.
			// 如果要为children的子元素指定默认类型, 应当在组件的getConcernedChildren静态方法中进行特别指定
			// 否则就应该在定义中进行特别指定
			childrenMapping = Object.assign(childrenMapping, type.getConcernedChildren());
		}

		const allChildren = Object.keys(childrenMapping)
			.map((key, index) => {
				// 从本组件的布局定义中把子组件的定义拿出来
				const childLayout = (layout as any)[key];
				if (childLayout == null) {
					// 并没有定义, 说明可以忽略
					// 返回null, 忽略这个元素
					// console.warn(
					// 	`Concerned child component[${key}] not defined.`
					// );
					return null;
				}

				return Utils.toArray(childLayout)
					.filter(elm => elm != null)
					.map((elementLayout, elementIndex) => {
						// 组件定义可以自己定义类型, 有时候可能会是子类型, 而不是本组件的基本定义类型
						// 如果没有自定义类型, 则使用本组件的基本定义
						const componentName = elementLayout.c || childrenMapping[key];
						// 找到子组件
						if (componentName == null) {
							console.error(key, elementLayout);
						}
						const type = Utils.isFunction(componentName)
							? componentName
							: this.getComponentRegistry().find(componentName);
						const element = React.createElement(
							type,
							this.buildLayoutForChild(type, elementLayout, index * 10000 + elementIndex),
							this.buildChildrenForChild(type, elementLayout)
						);
						return element;
					});
			})
			.filter(element => element != null);

		// 降维
		const elements = Array.prototype.concat.apply([], allChildren as []).filter(element => element != null);
		switch (elements.length) {
			case 0:
				return null;
			case 1:
				return elements[0];
			default:
				return elements;
		}
	}
	protected abstract getComponentRegistry(): any;
}

export { Container };

